/**
 * One graded question: the grades as a row of buttons in the Org Planning form's toggle style
 * (David, Oct 13 2026 — the radios read awkwardly), and under them the exact paragraph the chosen
 * grade puts in the letter — the template's own text, from the proxy
 * (`/reference-letter-form/wording`), never written here.
 *
 * It LOOKS like a toggle and BEHAVES as a radio group: exactly one grade, required, so the ARIA
 * radio pattern (radiogroup + radio + aria-checked, one tab stop, arrow keys move and select) — a
 * pressed toggle that cannot be un-pressed would tell a screen reader something untrue.
 */
import type { KeyboardEvent } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../shared';
import { choiceLabel } from './requestForm';
import { ERROR_TEXT } from './RequestFields';

const STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/**
 * @param wording grade → paragraph for this question, or null while there is none to show
 * @param fixed the paragraph is the same whatever the grade (the closing after an Employer Termination)
 */
export function GradeQuestion({
  id, label, hint, grades, value, error, wording, fixed, onChange,
}: {
  id: string;
  label: string;
  hint: string;
  grades: string[];
  value: string | null;
  error?: string;
  wording: Record<string, string> | null;
  fixed: boolean;
  onChange: (grade: string) => void;
}) {
  const paragraph = value && wording ? wording[value] : null;
  const errorId = `${id}-error`;
  const wordingId = `${id}-wording`;
  const optionId = (g: string) => `${id}-${g.toLowerCase().replace(/\s+/g, '-')}`;
  // The one tab stop: the chosen grade, else the first.
  const focusable = value && grades.includes(value) ? value : grades[0];

  /** Arrow keys move to the neighbouring grade and choose it, as in any radio group. */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = STEP[e.key];
    if (!step) return;
    e.preventDefault();
    const next = grades[(grades.indexOf(focusable) + step + grades.length) % grades.length];
    onChange(next);
    document.getElementById(optionId(next))?.focus();
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p id={`${id}-label`} className="text-sm font-semibold text-zinc-900">{label}</p>
        <p className="text-sm text-zinc-700">{hint}</p>
      </div>
      {/* As many columns as fit: a DS Button does not wrap its label, and "Satisfactory" needs ~8.5rem. */}
      <div
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onKeyDown={onKeyDown}
        className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-2"
      >
        {grades.map((g) => {
          const selected = value === g;
          return (
            <Button
              key={g}
              id={optionId(g)}
              type="button"
              size="md"
              role="radio"
              aria-checked={selected}
              // Read with the choice, where a screen reader announces it: the sentence it picks.
              aria-describedby={selected && paragraph ? wordingId : undefined}
              tabIndex={g === focusable ? 0 : -1}
              variant={selected ? 'primary' : 'outline'}
              className={cn('w-full rounded', error && !selected && 'border-red')}
              onClick={() => !selected && onChange(g)}
            >
              {choiceLabel(g)}
            </Button>
          );
        })}
      </div>
      {error && <p id={errorId} className={ERROR_TEXT}>{error}</p>}
      {paragraph ? (
        <blockquote id={wordingId} data-testid={wordingId} className="rounded border-y-0 border-l-4 border-r-0 border-solid border-trustedBlue bg-lightBlue px-4 py-3 text-sm leading-relaxed text-zinc-900">
          <span className="mb-1 block text-xs font-semibold text-zinc-700">
            {fixed ? 'In the letter, whatever the grade (after an employer termination the closing is always neutral)' : 'In the letter'}
          </span>
          {paragraph}
        </blockquote>
      ) : (
        wording && (
          <p className="text-sm text-zinc-700">
            {value ? 'This template has no sentence for that grade.' : 'Pick a grade to see the sentence it puts in the letter.'}
          </p>
        )
      )}
    </div>
  );
}
